import Swal from "sweetalert2";
import confirmationIcon from "../assets/bombilla.svg";
import closeCross from "../assets/close.svg";
import flechaAvanzar from "../assets/flecha-avanzar.svg";
import happyFace from "../assets/happy-face.svg";
import neutralFace from "../assets/neutral-face.svg";
import sadFace from "../assets/sad-face.svg";
import { IconX, IconCheck, IconArrowNarrowRight, IconCircleX } from "@tabler/icons-react";
import ReactDOMServer from "react-dom/server";
import pacienteTemporal from "../assets/avatares/paciente-temporal.png";
import { createRoot } from "react-dom/client";

export const logError = ({
	error,
	customMessage = "No custom message from backoffice",
}) => {
	error?.response?.status
		? console.log(
				"ERROR\n" +
					`Status: ${error.response.status}.\n` +
					`Status error: ${error.response.statusText}.\n` +
					`API Message: ${error.response.data?.message}\n` +
					`Backoffice Message: ${customMessage}`
		  )
		: console.log(
				"ERROR\n" +
					`Backoffice message: ${customMessage}\n` +
					`${error}`
		  );
};

/** Texto secundario para depuración (p. ej. iPad / red / qué endpoint falló). */
function buildErrorFooter(error, extraDetails) {
	const parts = [];
	if (extraDetails) parts.push(String(extraDetails));
	if (error && typeof error === "object") {
		if (error.code) parts.push(`code: ${error.code}`);
		if (error.response?.status != null)
			parts.push(`HTTP ${error.response.status}`);
		const cfg = error.config;
		if (cfg?.method && cfg?.url) {
			const path = cfg.url?.startsWith("http")
				? cfg.url
				: `${cfg.baseURL || ""}${cfg.url || ""}`;
			parts.push(`${String(cfg.method).toUpperCase()} ${path}`);
		} else if (error.message) {
			parts.push(error.message);
		}
	} else if (error != null && error !== "") {
		parts.push(String(error));
	}
	return parts.length ? parts.join(" · ") : "";
}

function escSwalPlainText(str) {
	return String(str ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");
}

export const alertError = ({ error, customMessage, details, appearance } = {}) => {
	if (appearance === "register") {
		const headline =
			(customMessage && String(customMessage).trim()) ||
			error?.response?.data?.message ||
			error?.response?.data?.error ||
			error?.response?.statusText ||
			(typeof error === "string" && String(error).trim() ? String(error).trim() : null) ||
			error?.message ||
			"Ha ocurrido un problema.";
		/* Registro: solo mensaje legible para el equipo (sin trazas HTTP). */
		const html = `<div class="register-swal-body text-center">
			<p class="register-swal-title">${escSwalPlainText(headline)}</p>
		</div>`;
		return Swal.fire({
			html,
			showConfirmButton: true,
			confirmButtonText: "Entendido",
			buttonsStyling: false,
			allowEnterKey: true,
			allowEscapeKey: true,
			backdrop: "rgba(0, 0, 0, 0.65)",
			customClass: {
				popup: "modal-confirm-container pb-5 modal-sg5 modal-register-alert",
				confirmButton: "register-swal-confirm",
				htmlContainer: "register-swal-html",
			},
		});
	}

	const message =
		error?.response?.data?.message ||
		error?.response?.data?.error ||
		error?.response?.statusText ||
		(typeof error === "string" ? error : error?.message) ||
		customMessage ||
		"Error has no message";

	const footerHtml = buildErrorFooter(error, details);

	const params = {
		icon: "error",
		title: (customMessage?.length < 70 && customMessage) || "Ooops...",
		text: message,
	};

	if (footerHtml) {
		const esc = footerHtml
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;");
		params.footer = `<small style="display:block;margin-top:.75em;opacity:.85;word-break:break-word;">${esc}</small>`;
	}

	Swal.fire(params);
};

export const alertResume = ({} = {}) => {
	const params = {
		icon: "question",
		title: "¿Quieres seguir con la partida predeterminada?",
		confirmButtonText: "Sí, continuar partida",
		cancelButtonText: "No, jugar la de pruebas",
		showCancelButton: true,
		reverseButtons: true,
		allowEnterKey: false,
		allowEscapeKey: false,
		allowOutsideClick: false,
		customClass: {
			popup: "modal-resolution-container",
			confirmButton: "btn-main w-fit-content",
			cancelButton: "btn-main-cancel w-fit-content",
		},
	};
	return Swal.fire(params);
};

export const alertGroup = ({ data } = {}) => {
	const html = `
	<div class='text-start'>
		<div class='bold'>Nombre: <span class='regular'>${
			data.group.fullName
		}</span></div>
		<div class='bold'>Mesa: <span class='regular'>${
			data.group.tableNum
		}</span></div>
		<div class='bold'>Participantes:<br/> ${data.members
			.map((member) => {
				return `<span class='ps-3 my-1 regular'>${member.fullName}<br/></span>
		`;
			})
			.join("")}</div>
	</div>`;
	const params = {
		icon: "question",
		html: html,
		title: "¿Es este tu grupo?",
		confirmButtonText: "Sí, continuar partida",
		cancelButtonText: "No, escoger otro",
		showCancelButton: true,
		reverseButtons: true,
		allowEnterKey: false,
		allowEscapeKey: false,
		allowOutsideClick: false,
		customClass: {
			confirmButton: "btn-main-confirm w-fit-content",
			cancelButton: "btn-main-cancel w-fit-content",
		},
	};
	return Swal.fire(params);
};

export const modalTableNum = ({ tableNum } = {}) => {
	let html = `<div class="p-4 d-flex flex-column align-items-center text-center txt22 txt-blue">
			<div>
				Se te ha asignado la mesa <span class="bold">${tableNum}.</span>
			</div>
			<div class="mt-3">¡Acuérdate del número por si hay cualquier problema!</div>
		</div>`;
	const params = {
		html: html || "",
		allowEnterKey: false,
		allowEscapeKey: false,
		allowOutsideClick: true,
		showCloseButton: true,
		showConfirmButton: true,
		showCancelButton: false,
		confirmButtonText: `<div class="d-flex align-items-center justify-content-center">DE ACUERDO</div>`,
		backdrop: "rgba(0,0,0, 0.7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: "modal-question-explanation",
			confirmButton: "btn-main",
			closeButton: "modal-btn-close",
		},
	};
	Swal.fire(params);
};

export const modalEnunciado = ({ title, html } = {}) => {
	const params = {
		title: title || "",
		html: html || "",
		allowEnterKey: false,
		allowEscapeKey: false,
		showCloseButton: true,
		showConfirmButton: false,
		showCancelButton: true,
		cancelButtonText: `<div class="d-flex align-items-center justify-content-center">
			<img class="me-2" src=${closeCross} alt="close"></img>CERRAR</div>`,
		backdrop: "rgba(0,0,0, 0.7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: "modal-enunciado-container",
			htmlContainer: "modal-enunciado-html",
			title: "modal-text-title",
			closeButton: "modal-btn-close",
			cancelButton: "btn-main-cancel",
		},
	};
	Swal.fire(params);
};

export const modalText = ({ title, html, showCancelButton } = {}) => {
	const params = {
		title: title || "",
		html: html || "",
		allowEnterKey: false,
		allowEscapeKey: false,
		showCloseButton: true,
		showConfirmButton: false,
		showCancelButton:
			showCancelButton !== undefined ? showCancelButton : false,
		cancelButtonText: `<div class="d-flex align-items-center justify-content-center">
			<img class="me-2" src=${closeCross} alt="close"></img>CERRAR</div>`,
		backdrop: "rgba(0,0,0, 0.7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: "modal-text-container",
			htmlContainer: "modal-text-html",
			title: "modal-text-title",
			closeButton: "modal-btn-close",
			cancelButton: "btn-main-cancel w-fit-content",
		},
	};
	Swal.fire(params);
};

/**
 * Selector de perfil para el comodín "Consultar especialista".
 * Devuelve { profileId } o null si cancela.
 */
export const modalSpecialistPick = ({
	title,
	html,
	showCancelButton = false,
	themePopupClass,
} = {}) => {
	return Swal.fire({
		title: title || "Consultar especialista",
		html: html || "",
		allowEnterKey: false,
		allowEscapeKey: true,
		showCloseButton: true,
		showCancelButton,
		showConfirmButton: false,
		cancelButtonText: "Cerrar",
		backdrop: "rgba(0,0,0, 0.7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: ["modal-confirm-container", "pb-3", "modal-sg5", themePopupClass]
				.filter(Boolean)
				.join(" ")
				.trim(),
			htmlContainer: "modal-text-html",
			title: "modal-text-title specialist-modal-title",
			closeButton: "modal-btn-close",
			cancelButton: "btn-main bold mb-3 shadow",
		},
	});
};

export const modalSpecialistConfirm = ({ title, html, confirmText = "Consultar", themePopupClass } = {}) => {
	return Swal.fire({
		title: title || "Confirmar consulta",
		html: html || "",
		allowEnterKey: false,
		allowEscapeKey: true,
		showCloseButton: true,
		showCancelButton: false,
		showConfirmButton: true,
		confirmButtonText: confirmText,
		backdrop: "rgba(0,0,0, 0.7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: ["modal-confirm-container", "pb-3", "modal-sg5", themePopupClass]
				.filter(Boolean)
				.join(" ")
				.trim(),
			htmlContainer: "modal-text-html",
			title: "modal-text-title specialist-modal-title",
			closeButton: "modal-btn-close",
			confirmButton: "btn-main bold mb-3 shadow bgConfirmButton",
		},
	});
};

export const modalSpecialistInfo = ({ title, html, themePopupClass } = {}) => {
	return Swal.fire({
		title: title || "",
		html: html || "",
		allowEnterKey: false,
		allowEscapeKey: true,
		showCloseButton: true,
		showCancelButton: false,
		showConfirmButton: true,
		confirmButtonText: "Cerrar",
		backdrop: "rgba(0,0,0, 0.7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: ["modal-confirm-container", "pb-3", "modal-sg5", themePopupClass]
				.filter(Boolean)
				.join(" ")
				.trim(),
			htmlContainer: "modal-text-html",
			title: "modal-text-title specialist-modal-title",
			closeButton: "modal-btn-close",
			confirmButton: "btn-main bold mb-3 shadow btn-main--swal-text specialist-close-btn",
		},
	});
};

export const modalPapel = ({ image } = {}) => {
	let html = `<div class="d-flex justify-content-center align-items-center text-center"><img src=${image}></img></div>`;
	const params = {
		html: html || "",
		allowOutsideClick: true,
		allowEnterKey: false,
		allowEscapeKey: false,
		showCloseButton: false,
		showConfirmButton: false,
		showCancelButton: true,
		cancelButtonText: `<div class="d-flex align-items-center justify-content-center">
			<img class="me-2" src=${closeCross} alt="close"></img>CERRAR</div>`,
		backdrop: "rgba(0,0,0, 0.7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: "modal-papel-container",
			closeButton: "modal-btn-close",
			cancelButton: "btn-main-cancel w-fit-content px-4",
		},
	};
	Swal.fire(params);
};

export const modalConfirm = ({ text, titleClass, estasseguro, color, themePopupClass } = {}) => {
	const frameAvatar =
		typeof themePopupClass === "string" && themePopupClass.includes("modal-sg5");
	const avatarHtml = estasseguro
		? frameAvatar
			? `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${estasseguro}" alt="" class="estasseguro swal-estasseguro-img" /></div></div>`
			: `<img src=${estasseguro} alt="¿Estan seguros?" class="estasseguro" />`
		: "";

	let html =
		`${avatarHtml}
		<div class="text-center txtTitulos mt-3 ${color}">
		¿Estás seguro de tu elección?
		</div>`;

	const fepolSwalConfirm =
		typeof themePopupClass === "string" &&
		themePopupClass.includes("modal-sg5--fepol");
	const checkIcon = ReactDOMServer.renderToStaticMarkup(
		<IconCheck
			size={50}
			color={fepolSwalConfirm ? "#003A70" : "white"}
			stroke={2}
		/>
	);
	const xIcon = ReactDOMServer.renderToStaticMarkup(
		<IconX size={50} color="white" stroke={2} />
	);
	const popupClasses = ["modal-confirm-container", "pb-5", themePopupClass].filter(Boolean).join(" ").trim();
	const params = {
		html: html,
		allowEnterKey: false,
		allowEscapeKey: false,
		reverseButtons: true,
		showCancelButton: true,
		confirmButtonText: `
			<div class="d-flex align-items-center justify-content-center">
				${checkIcon}
			</div>`,
		cancelButtonText: `
			<div class="d-flex align-items-center justify-content-center">
				${xIcon}
			</div>`,
		backdrop: "rgba(0,0,0, .7)",
		customClass: {
			popup: popupClasses,
			title: titleClass || "modal-confirm-title bold",
			confirmButton: "btn-main bold mb-3 shadow bgConfirmButton",
			cancelButton: "btn-main bold mb-3 shadow",
		},
	};
	return Swal.fire(params);
};

export const modalQuestionExplanation = ({
	question,
	answerHtml,
	showConfirmButton,
	allowOutsideClick,
	showCloseButton,
} = {}) => {
	let html =
		`<div class="text-center">
		<div class="txt22 txt-red bold">${question}</div>
		<br />
		<div class="txt18"${answerHtml}</div>	
	</div>`;
	const params = {
		html: html,
		allowOutsideClick:
			allowOutsideClick !== undefined ? allowOutsideClick : true,
		allowEnterKey: false,
		allowEscapeKey: false,
		showCloseButton: showCloseButton !== undefined ? showCloseButton : true,
		showConfirmButton:
			showConfirmButton !== undefined ? showConfirmButton : false,
		backdrop: "rgba(0,0,0, .7)",
		confirmButtonText: ``,
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: "modal-question-explanation",
			confirmButton: "btn-main w-fit-content",
			closeButton: "modal-btn-close",
		},
	};
	return Swal.fire(params);
};

export const modalQuestionResolution = ({
  question,
  answerHtml,
  textHtml,
  correct,
  allowOutsideClick,
  showConfirmButton,
  showCancelButton,
  customConfirmButtonHtml,
  customCancelButtonHtml,
  cancelFitContent,
  popupClass,
  themePopupClass,
  color,
  /** Si true, el HTML del confirm es solo icono → mantiene btn-main circular (sin pill de texto). */
  confirmIsIconButton,
  cancelIsIconButton,
} = {}) => {
  // estilos de bloque según correcto/incorrecto (tema clásico inline)
  const blockStyle = correct
    ? "background-color:green; border:1px solid #b1000d1f;color:white;"
    : "background-color:red; border:1px solid #e0e0e0;color:white !important;";

  const fepolModal =
    typeof themePopupClass === "string" &&
    themePopupClass.includes("modal-sg5--fepol");

  // iconos decorativos grandes (como en tu ejemplo de <IconClipboardList>)
  const confettiBgIcon = ReactDOMServer.renderToStaticMarkup(
    <IconCheck
      size={100}
      color={correct ? "#000" : "#000"}
      stroke={1}
      className="position-absolute"
      style={{
        top: "0px",
        right: "0px",
        opacity: 0.1,
        pointerEvents: "none",
      }}
    />
  );

  const xBgIcon = ReactDOMServer.renderToStaticMarkup(
    <IconX
      size={100}
      color="#000"
      stroke={1}
      className="position-absolute"
      style={{
        top: "0px",
        right: "0px",
        opacity: 0.1,
        pointerEvents: "none",
      }}
    />
  );

  // bloque de explicación con icono decorativo
  const explanationBlock =
    question && answerHtml
      ? fepolModal
        ? `<div class="position-relative mt-5 txt20 quiz-resolution-fepol quiz-resolution-fepol--${
            correct ? "ok" : "wrong"
          }" style="border-radius:12px; overflow:hidden;">
          <div class="mb-0 ps-3 text-start py-3 px-2">
            <div class="txt22 bold mb-3">${question}</div>
            <div class="txt18">${answerHtml}</div>
          </div>
          ${correct ? confettiBgIcon : xBgIcon}
        </div>`
        : `<div class="position-relative mt-5 txt20" style="${blockStyle} border-radius:8px; padding:20px; overflow:hidden;">
          <div class="mb-0 ps-3 text-start">
            <div class="txt22 bold mb-3">${question}</div>
            <div class="txt18">${answerHtml}</div>
          </div>
          ${correct ? confettiBgIcon : xBgIcon}
        </div>`
      : "";

  // contenido principal del modal
  let html = `
    <div class="text-center">
      <div>${textHtml}</div>
    </div>`;

  // iconos para los botones
  const confettiIcon = ReactDOMServer.renderToStaticMarkup(
    <IconArrowNarrowRight size={50} color="white" stroke={2} />
  );
  const xIcon = ReactDOMServer.renderToStaticMarkup(
    <IconX size={50} color="white" stroke={2} />
  );

  const wrapBtn = (inner) =>
    `<div class="d-flex align-items-center justify-content-center">${inner}</div>`;

  let finalShowConfirm = showConfirmButton;
  let finalShowCancel = showCancelButton;

  let confirmButtonText = "";
  let cancelButtonText = wrapBtn(correct ? confettiIcon : xIcon);

  if (customConfirmButtonHtml) {
    confirmButtonText = wrapBtn(customConfirmButtonHtml);
  }
  if (customCancelButtonHtml) {
    cancelButtonText = wrapBtn(customCancelButtonHtml);
  }

  if (finalShowConfirm === undefined || finalShowCancel === undefined) {
    if (customConfirmButtonHtml && customCancelButtonHtml) {
      if (finalShowConfirm === undefined) finalShowConfirm = true;
      if (finalShowCancel === undefined) finalShowCancel = true;
    } else if (customConfirmButtonHtml) {
      if (finalShowConfirm === undefined) finalShowConfirm = true;
      if (finalShowCancel === undefined) finalShowCancel = false;
    } else if (customCancelButtonHtml) {
      if (finalShowConfirm === undefined) finalShowConfirm = false;
      if (finalShowCancel === undefined) finalShowCancel = true;
    } else {
      if (finalShowConfirm === undefined) finalShowConfirm = false;
      if (finalShowCancel === undefined) finalShowCancel = true;
    }
  }

  const popupClasses = ["modal-confirm-container", "pb-5", popupClass, themePopupClass]
    .filter(Boolean)
    .join(" ")
    .trim();

  const confirmButtonClass = customConfirmButtonHtml
    ? confirmIsIconButton
      ? `btn-main bold shadow ${color || ""}`.trim()
      : `btn-main btn-main--swal-text bold shadow ${color || ""}`.trim()
    : `btn-main bold shadow ${color || ""}`.trim();
  const cancelButtonClass = customCancelButtonHtml
    ? cancelIsIconButton
      ? `btn-main bold shadow ${color || ""}`.trim()
      : `btn-main btn-main--swal-text bold shadow ${color || ""}`.trim()
    : `btn-main bold shadow ${color || ""}`.trim();

  const params = {
    html: html,
    allowOutsideClick: allowOutsideClick ?? false,
    allowEnterKey: false,
    allowEscapeKey: false,
    showCloseButton: false,
    showCancelButton: finalShowCancel ?? true,
    showConfirmButton: finalShowConfirm ?? false,
    confirmButtonText: confirmButtonText || undefined,
    cancelButtonText,
    backdrop: "rgba(0,0,0, .7)",
    customClass: {
      popup: popupClasses,
      confirmButton: confirmButtonClass,
      cancelButton: cancelButtonClass,
    },
  };

  return Swal.fire(params);
};


export const modalCasoClinico = ({ correct, textHtml } = {}) => {
	let html = `<div class="text-center">
    <div>
      <img src=${
			correct === null ? neutralFace : correct ? happyFace : sadFace
		} alt='cara' height="50"/>
    </div>
    <br />
    <div>${textHtml}</div>
  </div>`;

	const params = {
		html: html,
		allowOutsideClick: true,
		allowEnterKey: false,
		allowEscapeKey: false,
		showCloseButton: true,
		showCancelButton: false,
		showConfirmButton: false,
		backdrop: "rgba(0,0,0, .7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: "modal-caso-clinico-resolution",
			htmlContainer: "modal-caso-clinico-html",
			closeButton: "modal-btn-close",
		},
	};
	return Swal.fire(params);
};

export const modalSello = ({
	imageSello,
	textHtml,
	showConfirmButton,
	achieved,
} = {}) => {
	let html = `<div class="text-center d-flex flex-column align-items-center">
			<div>
				${achieved ? `<img src=${imageSello} alt='sello' width="55%"/>` : ""}
			</div>
			<div class="mt-3">${textHtml}</div>
		</div>`;
	const params = {
		html: html,
		allowEnterKey: false,
		allowEscapeKey: false,
		allowOutsideClick: true,
		showConfirmButton: showConfirmButton,
		showCloseButton: true,
		backdrop: "rgba(0,0,0, .7)",
		confirmButtonText: `<div class="d-flex align-items-center justify-content-center"><div class="text-btn-main">AVANZAR</div><img class="ms-2" src=${flechaAvanzar} alt="go"></img></div>`,
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: `modal-sello-container ${showConfirmButton ? "pb-5" : ""}`,
			confirmButton: "btn-main",
			closeButton: "modal-btn-close",
		},
	};
	return Swal.fire(params);
};

export const modalWait = ({ html }) => {
	let result = `<div class="d-flex flex-column align-items-center txt22 txtBlue">${html}</div>`;
	const params = {
		html: result,
		allowEnterKey: false,
		allowEscapeKey: false,
		confirmButtonText: `<div class="text-btn-main">OK</div>`,
		showCancelButton: false,
		customClass: {
			popup: "modal-wait-container",
			confirmButton: "btn-main bgRed",
		},
	};
	return Swal.fire(params);
};

export const modalPDF = ({
	pdf,
	title,
	showCloseButton,
	allowOutsideClick,
	customCancelButtonHtml,
} = {}) => {
	let html = `<iframe
			id="pdfviewer"
			src=${pdf}
			frameborder="0"
			width="90%"
			height="550px"
			type="application/pdf"
		></iframe>`;
	const params = {
		title: title || "CRITERIOS GLIM",
		html: html,
		allowEnterKey: false,
		allowEscapeKey: false,
		showCloseButton:
			showCloseButton === true || showCloseButton === false
				? showCloseButton
				: true,
		allowOutsideClick:
			allowOutsideClick === true || allowOutsideClick === false
				? allowOutsideClick
				: true,
		showCancelButton: true,
		showConfirmButton: false,
		cancelButtonText: customCancelButtonHtml
			? customCancelButtonHtml
			: `<div class="d-flex align-items-center justify-content-center">AVANZAR</div>`,
		backdrop: "rgba(0,0,0, .7)",
		closeButtonHtml: `<div class="modal-btn-close"></div>`,
		customClass: {
			popup: "modal-pdf-container",
			htmlContainer: "modal-pdf-html",
			title: "modal-text-title",
			cancelButton: "btn-main-cancel",
			closeButton: "modal-btn-close",
		},
	};
	return Swal.fire(params);
};

export const modalIntro = () => {
	let html = `
				<div class="w-100 px-4" style="text-align: left;">
					<h3 class="txtTitulos black bold mb-4">
						RECORDATORIO DEL JUEGO
					</h3>
					<p class="txtTexto1 black">
						Una barra inferior mostrará su progreso y las actividades pendientes.
					</p>
					<p class="txtTexto1 black">
						Sistema económico:
						<ul>
							<li>
								Respuesta correcta: <strong>+10 pt</strong>
							</li>
							<li>
								Respuesta incorrecta: <strong>−2 pt</strong>
							</li>
							<li>
								Segundo intento correcto: <strong>+3 pt</strong>
							</li>
							<li>
								Uso de pista: <strong>−3 pt</strong>
							</li>
						</ul>
					</p>
					<p class="txtTexto1 black">
						Tiempo límite: <strong>8 minutos</strong> para completar la dinámica.
					</p>
					<p class="txtTexto1 black">
						Gana el equipo con mayor puntaje. En caso de empate, se premiará al que haya resuelto el caso en <strong>menos tiempo</strong>.
					</p>
				</div>
	`;
	const params = {
		html: html || "",
		allowEnterKey: false,
		allowEscapeKey: false,
		showCloseButton: true,
		showConfirmButton: false,
		backdrop: "rgba(0,0,0, .7)",
		closeButtonHtml: `<div class="modal-btn-close-video"></div>`,
		customClass: {
			popup: "modal-video-container",
			htmlContainer: "modal-video-html",
			closeButton: "modal-btn-close-video",
		},
	};
	Swal.fire(params);
};

export const modalPresentacionManuel = (
	transcription,
	videoSrc,
	videoSrcMp4,
	onNext,
	flechaAvanzarSrc,
	canPlayWithSound = false,
	defaultVolume = 0.8,
	color,
	fullVideo = false,
	popupThemeClass = null
) => {
  const BTN_ID = "btn-next";
  let nextFallbackTimer = null;
  const clearNextFallback = () => {
    if (nextFallbackTimer != null) {
      clearTimeout(nextFallbackTimer);
      nextFallbackTimer = null;
    }
  };
  let bgcolor = "";

  switch (color) {
	case "orange":
		bgcolor = "bgOrange"
		break;
	case "violet":
		bgcolor = "bgViolet"
		break;
	case "blue":
		bgcolor = "bgBlue"
		break;
	case "green":
		bgcolor = "bgGreen"
		break;
	default:
		break;
  }

	const useSg5VideoModal = Boolean(popupThemeClass);
	const videoClassInner = fullVideo
		? "video-presentacion-manuel w-100"
		: useSg5VideoModal
			? "video-presentacion-manuel"
			: "video-presentacion-manuel w-50";
	const rowClass = fullVideo
		? ""
		: useSg5VideoModal
			? "modal-manuel-layout-sg5 d-flex"
			: "d-flex justify-content-between";

	const videoTag = `
  	  <video
        class="${videoClassInner}"
        autoplay
        playsinline
		webkit-playsinline
        preload="auto"
		${canPlayWithSound ? "" : "muted"}
      >
	  	<source src="${videoSrcMp4}"  type="video/mp4" />
        <source src="${videoSrc}" type="video/webm" />
      </video>`;

	const video =
		useSg5VideoModal && !fullVideo
			? `<div class="sg5-video-shell modal-manuel-sg5-shell"><div class="start-game-avatar-inner start-game-avatar-inner--silent sg5-video-inner">${videoTag}</div></div>`
			: videoTag;

  let html = "";
  
  if (fullVideo) {
	html = `
		<div class="">
			${video}
			<div class="d-flex justify-content-end align-items-center mt-3">
					<div class="txtBotones txtRed me-3 ${color}">Siguiente</div>
					<button
					id="${BTN_ID}"
					class="btn-main ${bgcolor}${useSg5VideoModal ? " modal-manuel-next-sg5" : ""}"
					style="width:80px;height:80px;border-radius:50%;"
					>
					<img
						src="${flechaAvanzarSrc}"
						alt="Siguiente"
						style="max-width:40px;"
					/>
					</button>
				</div>
		</div>`;
  } else {
	html = `
		<div class="${rowClass}">
		${video}
		<div class="d-flex align-items-center modal-manuel-text-col">
			<div class="text-left px-4">
			<p class="txtTexto1 black">
				${transcription}
			</p>

			<!-- Botón con el estilo que quieres -->
			<div class="d-flex justify-content-end align-items-center mt-3">
				<div class="txtBotones txtRed me-3 ${color}">Siguiente</div>
				<button
				id="${BTN_ID}"
				class="btn-main ${bgcolor}${useSg5VideoModal ? " modal-manuel-next-sg5" : ""}"
				style="width:80px;height:80px;border-radius:50%;"
				>
				<img
					src="${flechaAvanzarSrc}"
					alt="Siguiente"
					style="max-width:40px;"
				/>
				</button>
			</div>
			</div>
		</div>
		</div>
	`;
  }
  

  Swal.fire({
    html,
    allowEnterKey: false,
    allowEscapeKey: false,
    showCloseButton: false,
    showConfirmButton: false,
    backdrop: "rgba(0,0,0, .7)",
    closeButtonHtml: `<div class="modal-btn-close-video"></div>`,
    customClass: {
      popup: popupThemeClass
				? `modal-video-container ${popupThemeClass}`
				: "modal-video-container",
      htmlContainer: "modal-video-html",
      closeButton: "modal-btn-close-video",
    },
    willClose: () => {
      clearNextFallback();
    },
    didOpen: async () => {
      const btn = document.getElementById(BTN_ID);
      let nextUnlocked = false;

      const applyNextLockedVisual = (locked) => {
        if (!btn) return;
        btn.disabled = locked;
        btn.setAttribute("aria-disabled", locked ? "true" : "false");
        btn.style.opacity = locked ? "0.4" : "";
        btn.style.pointerEvents = locked ? "none" : "";
        btn.classList.toggle("is-disabled", locked);
      };

      const enableNext = () => {
        if (nextUnlocked) return;
        nextUnlocked = true;
        clearNextFallback();
        applyNextLockedVisual(false);
      };

      /**
       * Si no llega `ended` (red, vídeo atascado, etc.), desbloquea tras duración + margen.
       * No hay duración “precalculada” por archivo en build: se usa `video.duration` del navegador
       * cuando existe; si aún no hay metadata, se asume un clip corto (~25 s típico en este juego).
       */
      const FALLBACK_BUFFER_MS = 6000;
      const FALLBACK_ASSUMED_DURATION_MS = 26000; // ~25 s + un poco, antes de conocer duration
      const FALLBACK_MIN_MS = 12000;
      const FALLBACK_MAX_MS = 120000; // tope 2 min por si un clip pesa más de lo esperado
      const scheduleFallbackUnlock = (videoEl) => {
        clearNextFallback();
        const d = videoEl?.duration;
        const durMs = Number.isFinite(d) && d > 0
          ? Math.ceil(d * 1000)
          : FALLBACK_ASSUMED_DURATION_MS;
        const ms = Math.min(
          FALLBACK_MAX_MS,
          Math.max(FALLBACK_MIN_MS, durMs + FALLBACK_BUFFER_MS)
        );
        nextFallbackTimer = window.setTimeout(enableNext, ms);
      };

      applyNextLockedVisual(true);
      if (btn) {
        btn.addEventListener("click", (e) => {
          if (!nextUnlocked) return;
          Swal.close();
          if (onNext) onNext(e);
        });
      }
	  // Forzar control del <video> insertado por SweetAlert
	  const videoEl = document.querySelector(".video-presentacion-manuel");
	  if (!videoEl) {
		enableNext();
		return;
	  }

	  videoEl.addEventListener("ended", enableNext, { once: true });
	  videoEl.addEventListener("error", enableNext, { once: true });
	  scheduleFallbackUnlock(videoEl);

		// Atributos críticos para iOS
		videoEl.setAttribute("playsinline", "");
		videoEl.setAttribute("webkit-playsinline", "");

		try {
			if (canPlayWithSound) {
			videoEl.muted = false;
			videoEl.removeAttribute("muted");
			videoEl.volume = defaultVolume;
			} else {
			videoEl.muted = true;
			videoEl.volume = 0.0;
			}
			await videoEl.play();
		} catch (err1) {
			// Si falla, reintenta en muted para evitar “pantalla en blanco”
			try {
			videoEl.muted = true;
			videoEl.volume = 0.0;
			await videoEl.play();
			} catch (err2) {
			// último recurso: pedir tap manual
			alert("⚠️ Toca el vídeo para iniciarlo.");
			}
		}

		// Si el recurso carga después, remata el desmuteo si ya estaba desbloqueado
		const onLoaded = async () => {
			if (canPlayWithSound) {
			try {
				videoEl.muted = false;
				videoEl.removeAttribute("muted");
				videoEl.volume = defaultVolume;
				if (videoEl.paused) await videoEl.play();
			} catch {}
			}
		};
		videoEl.addEventListener(
			"loadedmetadata",
			async () => {
				await onLoaded();
				scheduleFallbackUnlock(videoEl);
			},
			{ once: true }
		);

		// Tap de respaldo
		videoEl.addEventListener("click", async () => {
			try {
			if (canPlayWithSound) {
				videoEl.muted = false;
				videoEl.removeAttribute("muted");
				videoEl.volume = defaultVolume;
			}
			if (videoEl.paused) await videoEl.play();
			} catch {}
		}, { once: true });
    },
  });
};

/**
 * @param {string} [hintMarkHtml] Si se pasa, sustituye solo el icono superior del **segundo** modal
 * (texto de la pista ya revelada). El primer modal sigue usando `image` (avatar / ilustración).
 */
export const modalHint = ({
	image,
	imageHintUsed,
	hint,
	color,
	big = false,
	themePopupClass,
	hintMarkHtml,
} = {}) => {
	const frameHintAvatar =
		typeof themePopupClass === "string" && themePopupClass.includes("modal-sg5");
	const hintConfirmImage = frameHintAvatar
		? `<div class="swal-estasseguro-shell"><div class="swal-estasseguro-inner"><img src="${image}" alt="" class="imageHint swal-estasseguro-img" /></div></div>`
		: `<img src="${image}" alt="Usar pista" class="imageHint" />`;

  const htmlFirst = `
    ${hintConfirmImage}
    <div class="text-center txtTitulos mt-2 ${color} swal-hint-confirm-question">
      ¿Estás seguro de que quieres utilizar la pista?
    </div>
    <div class="text-center txtText01">
      Recuerda que si utilizas la pista se te restan 3 pt
    </div>`;

  let bgcolor = "";
  switch (color) {
    case "orange": bgcolor = "bgOrange"; break;
    case "violet": bgcolor = "bgViolet"; break;
    case "blue":   bgcolor = "bgBlue";   break;
    case "green":  bgcolor = "bgGreen";  break;
    default: break;
  }

  const isImage = (val) =>
    typeof val === "string" &&
    (/\.(png|jpe?g|gif|webp|svg)$/i.test(val) || val.startsWith("data:"));

  const firstParams = {
    html: htmlFirst,
    allowEnterKey: false,
    allowEscapeKey: false,
    reverseButtons: true,
    showCancelButton: true,
    showConfirmButton: true,
    confirmButtonText: `
      <div class="d-flex align-items-center justify-content-center">
        Utilizarla
      </div>`,
    cancelButtonText: `
      <div class="d-flex align-items-center justify-content-center">
        No utilizarla
      </div>`,
    backdrop: "rgba(0,0,0, .7)",
    customClass: {
      popup: ["modal-confirm-container", "pb-5", themePopupClass].filter(Boolean).join(" ").trim(),
      title: "modal-confirm-title bold",
      confirmButton: `${bgcolor} btnConfirmPista bold mb-3 shadow`,
      cancelButton: "btnConfirmPista bold mb-3 shadow",
    },
  };

  return Swal.fire(firstParams).then((res) => {
    if (!res.isConfirmed) return { used: false };

    // Segundo modal: decorativo + pista (texto o imagen)
    const hintHtml = isImage(hint)
      ? `<img src="${hint}" alt="Pista" class="hintContentImage" />`
      : `<div class="hintContentText txt20">${typeof hint === "string" ? hint : ""}</div>`;

    const hintMarkBlock = hintMarkHtml
      ? `<div class="hint-mark-wrap hint-mark-wrap--plain hint-mark-wrap--in-modal">${hintMarkHtml}</div>`
      : imageHintUsed
        ? `<img src="${imageHintUsed}" alt="Icono pista" class="imageHintUsed" />`
        : "";

    const htmlSecond = `
      <div class="hintModal">
        ${hintMarkBlock}
        ${hintHtml}
      </div>`;
	  
  	const hintPopupClass = `${big ? "modal-confirm-container-big" : "modal-confirm-container"} pb-5 ${themePopupClass || ""}`.trim();

    const secondParams = {
      html: htmlSecond,
      allowOutsideClick: false,
      allowEnterKey: false,
      allowEscapeKey: false,
      showCancelButton: false,
      showConfirmButton: true,
      confirmButtonText: "Cerrar",
      backdrop: "rgba(0,0,0, .7)",
      customClass: {
        popup: hintPopupClass,
        confirmButton: `${bgcolor} btnConfirmPista bold mb-3 shadow`,
      },
    };

    return Swal.fire(secondParams).then(() => ({ used: true }));
  });
};


/** @param {string} [hintMarkHtml] Si se pasa, sustituye la imagen superior por icono + «Pista» (p. ej. lupa). */
export const modalHintShow = ({
	hint,
	imageHintUsed,
	color,
	big = false,
	themePopupClass,
	hintMarkHtml,
} = {}) => {
  const bgcolor =
    color === "orange" ? "bgOrange" :
    color === "violet" ? "bgViolet" :
    color === "blue"   ? "bgBlue"   :
    color === "green"  ? "bgGreen"  : "";

  const isImage = (val) =>
    typeof val === "string" &&
    (/\.(png|jpe?g|gif|webp|svg)$/i.test(val) || val.startsWith("data:"));

  const hintContent = isImage(hint)
    ? `<img src="${hint}" alt="Pista" class="hintContentImage" />`
    : `<div class="hintContentText txt20">${hint || ""}</div>`;

  const hintMarkBlock = hintMarkHtml
    ? `<div class="hint-mark-wrap hint-mark-wrap--plain hint-mark-wrap--in-modal">${hintMarkHtml}</div>`
    : imageHintUsed
      ? `<img src="${imageHintUsed}" alt="Icono pista" class="imageHintUsed" />`
      : "";

  const html = `
    <div class="hintModal">
      ${hintMarkBlock}
      ${hintContent}
    </div>`;

  const hintPopupClass = `${big ? "modal-confirm-container-big" : "modal-confirm-container"} pb-5 ${themePopupClass || ""}`.trim();

  const params = {
    html,
    allowOutsideClick: false,
    allowEnterKey: false,
    allowEscapeKey: false,
    showCancelButton: false,
    showConfirmButton: true,
    confirmButtonText: "Cerrar",
    backdrop: "rgba(0,0,0, .7)",
    customClass: {
      popup: hintPopupClass,
      confirmButton: `${bgcolor} btnConfirmPista bold mb-3 shadow`,
    },
  };

  return Swal.fire(params);
};

